import type { NonCancelableCustomEvent } from '@risk-smart/themed-cloudscape-components/interfaces';
import type { TableProps } from '@risk-smart/themed-cloudscape-components/table';
import { useRating } from '@risksmart-app/components/src/hooks/useRating';
import SimpleRatingBadge from '@risksmart-app/components/src/simple-rating-badge/SimpleRatingBadge';
import { ParentTypes } from '@risksmart-app/domain/src/types/consts';
import { IndicatorType } from '@risksmart-app/domain/src/types/consts/indicator-type';
import _ from 'lodash';
import type { MutableRefObject } from 'react';
import { useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { MAX_COL_WIDTH } from 'src/App.config';

import Link from '@/components/link';
import PolarityBadge from '@/components/polarity-badge/PolarityBadge';
import type { IndicatorBandingConfigById } from '@/hooks/queries/indicator/useGetIndicatorBandingConfigsByIds';
import { useGetIndicatorBandingConfigsByIds } from '@/hooks/queries/indicator/useGetIndicatorBandingConfigsByIds';
import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';
import { useGetContributorsFieldConfig } from '@/utils/table/hooks/useGetContributorsFieldConfig';
import { useGetDepartmentFieldConfig } from '@/utils/table/hooks/useGetDepartmentFieldConfig';
import { useGetOwnersFieldConfig } from '@/utils/table/hooks/useGetOwnersFieldConfig';
import type { StatefulTableOptions } from '@/utils/table/hooks/useGetStatelessTableProps';
import type { UseGetTablePropsOptions } from '@/utils/table/hooks/useGetStatelessTableProps';
import { useGetStatelessTableProps } from '@/utils/table/hooks/useGetStatelessTableProps';
import { useGetTableProps } from '@/utils/table/hooks/useGetTableProps';
import { useGetTagFieldConfig } from '@/utils/table/hooks/useGetTagFieldConfig';
import { useGetViewersFieldConfig } from '@/utils/table/hooks/useGetViewersFieldConfig';
import { exportStyleFromValue } from '@/utils/table/pdfExportStyles';
import type { TableFields, TablePropsWithActions } from '@/utils/table/types';
import { dateColumnFromConfig } from '@/utils/table/utils/dateColumn';
import { friendlyIdColumn } from '@/utils/table/utils/friendlyIdColumn';
import { indicatorDetailsUrl } from '@/utils/urls';

import { conformanceRatingFromResults } from './calculateConformanceRating';
import {
  latestResultValueFromData,
  previousResultValueFromData,
} from './latestResultValueFromData';
import { resolveFrozenBandDisplay } from './resolveFrozenBandDisplay';
import { compareThreeMonthAverage } from './threeMonthAverage';
import type { IndicatorFlatFields, IndicatorTableFields } from './types';
import { ConformanceIndicatorRating } from './types';
import { useLabelledFields } from './useLabelledFields';

const useGetFieldConfig = (
  // Mutable ref the parent hook keeps in sync with the table's live sort
  // direction. Consumed by the Three Month Average comparator to pin
  // `—` / `N/A` rows to the bottom in both directions (AC-l-a + AC-l-b).
  sortingDescendingRef: MutableRefObject<boolean>,
  // CUS-188: `configurable_thresholds` flag state + the banding configs
  // pinned by the currently-visible rows' latest results, batch-fetched by
  // the caller (which has `records`) — see resolveFrozenBandDisplay.
  isConfigurableThresholdsEnabled: boolean,
  configById: IndicatorBandingConfigById
): TableFields<IndicatorTableFields> => {
  const allOwners = useGetOwnersFieldConfig<IndicatorTableFields>({
    formId: 'indicator',
    fieldId: 'Owners',
  });
  const allContributors = useGetContributorsFieldConfig<IndicatorTableFields>({
    formId: 'indicator',
    fieldId: 'Contributors',
  });
  const allViewers = useGetViewersFieldConfig<IndicatorTableFields>({
    formId: 'indicator',
    fieldId: 'Viewers',
  });
  const { getByValue: statusGetByValue } = useRating(
    'indicator_conformance_status'
  );
  const { getByValue: trendGetByValue } = useRating(
    'indicator_conformance_trend'
  );
  const { getByValue: getByValueTestScheduleStatus } = useRating(
    'test_schedule_status'
  );
  const { t: stc } = useTranslation(['common'], { keyPrefix: 'columns' });
  const { t: st } = useTranslation(['common'], {
    keyPrefix: 'indicators.columns',
  });
  const tagField = useGetTagFieldConfig<IndicatorTableFields>({
    formId: 'indicator',
    fieldId: 'tags',
  });
  const departmentField = useGetDepartmentFieldConfig<IndicatorTableFields>(
    (r) => r.departments,
    {
      formId: 'indicator',
      fieldId: 'departments',
    }
  );

  return useMemo(
    () => ({
      SequentialIdLabel: friendlyIdColumn<IndicatorTableFields>({
        header: stc('id'),
        parentType: ParentTypes.Indicator,
        hrefFor: (item) => indicatorDetailsUrl(item.Id),
      }),
      Title: {
        formId: 'indicator',
        fieldId: 'Title',
        cell: (item) => (
          <Link variant={'secondary'} href={indicatorDetailsUrl(item.Id)}>
            {item.Title}
          </Link>
        ),
        maxWidth: MAX_COL_WIDTH,
        isRowHeader: true,
      },
      ParentTitle: {
        header: st('parent_title'),
        maxWidth: MAX_COL_WIDTH,
      },
      TestFrequencyLabelled: {
        header: st('test_frequency'),
      },
      ConformanceLabelled: {
        header: st('conformance'),
        cell: (item) => {
          // Numeric only: text/boolean results are never band-stamped
          // (CUS-185 seeds and stamps `number` indicators alone), so running
          // them through the frozen path would render every one "Not set"
          // instead of the legacy target-match rating the flag-off build
          // still shows.
          if (
            isConfigurableThresholdsEnabled &&
            item.Type === IndicatorType.Number
          ) {
            const latestResult = item.orderedResults[0];
            const display = latestResult
              ? resolveFrozenBandDisplay(latestResult, configById)
              : null;

            return display ? (
              <PolarityBadge
                label={display.label}
                colour={display.colour}
                polarity={display.polarity}
                severity={display.severity}
              />
            ) : (
              <SimpleRatingBadge
                rating={statusGetByValue(ConformanceIndicatorRating.NotSet)}
              />
            );
          }

          const rating = conformanceRatingFromResults(item);

          return <SimpleRatingBadge rating={statusGetByValue(rating)} />;
        },
        // PDF export: colour cell using the conformance rating colour. Uses
        // the legacy live-evaluated rating even when configurable_thresholds
        // is on — export styling for frozen stamps is out of scope here.
        exportCellStyle: exportStyleFromValue(
          (item) => conformanceRatingFromResults(item),
          (v) => statusGetByValue(v)
        ),
      },
      LatestResultLabelled: {
        header: st('latest_result'),
        cell: (item) => {
          const result = latestResultValueFromData(item);
          if (_.isNil(result) || result === '') {
            return '-';
          }
          if (item.Unit) {
            return `${result} ${item.Unit}`;
          }

          return result;
        },
      },
      ThreeMonthAverage: {
        header: st('three_month_average'),
        headerTooltip: st('three_month_average_header_tooltip'),
        cell: (item) => {
          if (item.ThreeMonthAverageState === 'empty') {
            return (
              <span title={st('three_month_average_empty_tooltip')}>{'—'}</span>
            );
          }

          return item.ThreeMonthAverage;
        },
        // CSV (AC-j): empty-window exports as empty string, not the em-dash glyph.
        exportVal: (item) =>
          item.ThreeMonthAverageState === 'empty' ? '' : item.ThreeMonthAverage,
        // PDF (AC-k): empty-window shows the em-dash to match the rendered cell.
        pdfExportVal: (item) => item.ThreeMonthAverage,
        // AC-l-a + AC-l-b: comparator reads the live sort direction from
        // the parent-managed ref so `—`/`N/A` rows pin to the bottom in
        // both directions. See `compareThreeMonthAverage` for the rationale.
        sortingComparator: (a, b) =>
          compareThreeMonthAverage(
            a.ThreeMonthAverageValue,
            b.ThreeMonthAverageValue,
            sortingDescendingRef.current
          ),
        // AC-n: not a filter option.
        excludeFromFilter: true,
        fieldType: 'number',
      },
      PreviousResultLabelled: {
        header: st('previous_result'),
        cell: (item) => {
          const result = previousResultValueFromData(item);
          if (_.isNil(result) || result === '') {
            return '-';
          }
          if (item.Unit) {
            return `${result} ${item.Unit}`;
          }

          return result;
        },
      },
      LatestResultDateLabelled: dateColumnFromConfig({
        header: { header: st('latest_result_date') },
        dateField: 'LatestResultDateLabelled',
      }),
      NextTestDate: dateColumnFromConfig({
        header: { header: st('nextTestDate') },
        dateField: 'NextTestDate',
      }),
      NextTestOverdueDate: dateColumnFromConfig({
        header: { header: st('nextTestOverdue') },
        dateField: 'NextTestOverdueDate',
      }),
      TestScheduleStatusLabelled: {
        header: st('testScheduleStatus'),
        cell: (item) => {
          if (!item.TestScheduleStatus || item.TestScheduleStatus === '-') {
            return '-';
          }

          return (
            <SimpleRatingBadge
              rating={getByValueTestScheduleStatus(item.TestScheduleStatus)}
            >
              {item.TestScheduleStatusLabelled}
            </SimpleRatingBadge>
          );
        },
      },
      Unit: {
        formId: 'indicator',
        fieldId: 'Unit',
        cell: (item) => item.Unit || '-',
      },
      Description: {
        formId: 'indicator',
        fieldId: 'Description',
        cell: (item) => item.Description ?? '',
      },
      CreatedAtTimestamp: dateColumnFromConfig({
        header: { header: stc('created_on') },
        dateField: 'CreatedAtTimestamp',
      }),
      ModifiedAtTimestamp: dateColumnFromConfig({
        header: { header: stc('updated_on') },
        dateField: 'ModifiedAtTimestamp',
      }),
      allOwners,
      allContributors,
      allViewers,
      ParentType: {
        header: st('parent_type'),
        cell: (item) => item.ParentType,
      },
      ModifiedByUser: {
        header: stc('updated_by_id'),
      },
      ModifiedByUserName: {
        header: stc('updated_by_username'),
      },
      Id: {
        header: stc('guid'),
      },
      CreatedByUser: {
        header: stc('created_by_id'),
      },
      CreatedByUserName: {
        header: stc('created_by_username'),
      },
      LowerToleranceNum: {
        formId: 'indicator',
        fieldId: 'LowerToleranceNum',
      },
      UpperToleranceNum: {
        formId: 'indicator',
        fieldId: 'UpperToleranceNum',
      },
      LowerAppetiteNum: {
        formId: 'indicator',
        fieldId: 'LowerAppetiteNum',
      },
      UpperAppetiteNum: {
        formId: 'indicator',
        fieldId: 'UpperAppetiteNum',
      },
      TargetValueTxt: {
        formId: 'indicator',
        fieldId: 'TargetValueTxt',
      },
      tags: tagField,
      departments: departmentField,
      ConformanceTrend: {
        header: st('conformance_trend'),
        cell: (item) => {
          const rating = trendGetByValue(item.ConformanceTrendValue);

          return (
            <SimpleRatingBadge rating={rating}>
              {item.ConformanceTrend}
            </SimpleRatingBadge>
          );
        },
        // PDF export: colour cell using the trend rating colour
        exportCellStyle: exportStyleFromValue(
          (item) => item.ConformanceTrendValue,
          (v) => trendGetByValue(v)
        ),
      },
    }),
    [
      sortingDescendingRef,
      isConfigurableThresholdsEnabled,
      configById,
      allContributors,
      allOwners,
      allViewers,
      departmentField,
      st,
      statusGetByValue,
      stc,
      tagField,
      trendGetByValue,
      getByValueTestScheduleStatus,
    ]
  );
};

const useGetIndicatorTableProps = (
  records: IndicatorFlatFields[] | undefined,
  sortingDescendingRef: MutableRefObject<boolean>
): UseGetTablePropsOptions<IndicatorTableFields> => {
  const { t: stc } = useTranslation(['common']);

  const isConfigurableThresholdsEnabled = useIsFeatureFlagEnabled(
    'configurable_thresholds'
  );
  // Distinct pinned config ids across every visible row's latest result —
  // batched into one fetch rather than one per row.
  const distinctConfigIds = useMemo(
    () => [
      ...new Set(
        (records ?? []).flatMap((r) => {
          const id = r.orderedResults[0]?.BandingConfigId;

          return id ? [id] : [];
        })
      ),
    ],
    [records]
  );
  const { configById } = useGetIndicatorBandingConfigsByIds(distinctConfigIds, {
    isSkipped: !isConfigurableThresholdsEnabled,
  });

  const fields = useGetFieldConfig(
    sortingDescendingRef,
    isConfigurableThresholdsEnabled,
    configById
  );
  const labelledFields = useLabelledFields(
    records,
    isConfigurableThresholdsEnabled
  );

  return useMemo(
    () => ({
      customAttributeFormIds: ['indicator'],
      tableId: 'indicatorRegister',
      data: labelledFields,
      entityLabel: stc('indicators.entity_name'),
      emptyCollectionAction: <></>,
      preferencesStorageKey: 'IndicatorsRegisterTable-PreferencesV1',
      enableFiltering: true,
      initialColumns: [
        'SequentialIdLabel',
        'Title',
        'ParentTitle',
        'TestFrequencyLabelled',
        'LatestResultLabelled',
        'ConformanceLabelled',
        'ConformanceTrend',
        'LatestResultDateLabelled',
      ],
      fields,
    }),
    [fields, labelledFields, stc]
  );
};

/**
 * Wrap `tableProps.onSortingChange` so the ref consumed by the Three Month
 * Average comparator stays in sync with the live sort direction. The ref
 * MUST be updated synchronously before the wrapped handler fires
 * `setSortingState` — only then is it guaranteed to be current by the time
 * Cloudscape re-runs the comparator on the next render.
 */
const useSortingDirectionTracker = (
  tableProps: TablePropsWithActions<IndicatorTableFields>,
  sortingDescendingRef: MutableRefObject<boolean>
): TablePropsWithActions<IndicatorTableFields> => {
  const { onSortingChange: original } = tableProps;
  const onSortingChange = useCallback(
    (
      event: NonCancelableCustomEvent<
        TableProps.SortingState<IndicatorTableFields>
      >
    ) => {
      sortingDescendingRef.current = event.detail.isDescending ?? false;
      original?.(event);
    },
    [original, sortingDescendingRef]
  );

  return useMemo(
    () => ({ ...tableProps, onSortingChange }),
    [tableProps, onSortingChange]
  );
};

export const useGetCollectionTableProps = (
  records: IndicatorFlatFields[] | undefined
): TablePropsWithActions<IndicatorTableFields> => {
  const sortingDescendingRef = useRef(false);
  const props = useGetIndicatorTableProps(records, sortingDescendingRef);
  const tableProps = useGetTableProps(props);

  return useSortingDirectionTracker(tableProps, sortingDescendingRef);
};

export const useGetIndicatorSmartWidgetTableProps = (
  records: IndicatorFlatFields[] | undefined,
  statefulTableOptions: StatefulTableOptions<IndicatorTableFields>
): TablePropsWithActions<IndicatorTableFields> => {
  const sortingDescendingRef = useRef(false);
  const props = useGetIndicatorTableProps(records, sortingDescendingRef);
  const tableProps = useGetStatelessTableProps<IndicatorTableFields>({
    ...props,
    ...statefulTableOptions,
    enableFiltering: false,
  });

  return useSortingDirectionTracker(tableProps, sortingDescendingRef);
};
